import { gt } from 'common/PatternMatchingUtilities';
import { isDefined } from 'common/TypeUtilities';
import { urlDateToDateTime } from 'common/UrlDate';
import { flow, map, sortBy, uniqBy, values } from 'lodash';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { RadioContainer, RadioHeader, RadioInfo } from 'src/pages/components/ShefProfileRadioButtons';
import { ButtonSizes, PrimaryButton } from 'src/shared/design-system/Buttons';
import { ShefHat } from 'src/shared/design-system/Icon';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'src/shared/design-system/Modal';
import { ModalSizeEnum } from 'src/shared/design-system/Modal/content';
import { Radio } from 'src/shared/design-system/Radio';
import { Paragraph } from 'src/shared/design-system/Text';
import { useSelector } from 'src/store';
import { selectShefAndFoodItemUnavailabilityCounts } from 'src/store/cart/selectors';
import { LoadedCartShef } from 'src/store/cart/types';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { Colors, Font, Spacing } from 'web-common/src/shared/styles';

const StyledRadioContainer = styled(RadioContainer)`
  background: none;

  ${Radio} {
    padding-left: 0;
  }
`;

const StyledModal = styled(Modal)`
  padding: ${Spacing.SINGLE_HALF};
  max-height: 80vh;
`;

const Header = styled(ModalHeader)`
  margin-top: ${Spacing.QUARTER};
  margin-bottom: ${Spacing.SINGLE};
  font-size: ${Font.Size.L};
  font-weight: ${Font.Weight.SEMI_BOLD};
`;

const ShefBadge = styled.p`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 20px;
  margin-left: 8px;
  background: ${Colors.BELL_PEPPER_LITE_30};
  color: ${Colors.RADISH};
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 12px;
  margin: 0 0 0 8px;
  font-weight: ${Font.Weight.SEMI_BOLD};
`;

const ShefHatIcon = styled(ShefHat)`
  width: 14px;
  height: 14px;
  margin-top: -1px;
  margin-right: 4px;
`;

const ShefCount = styled.span`
  margin-top: 2px;
`;

interface EditOrderDeliveryModalProps {
  onClose: () => void;
  onDeliveryDateChange: (payload: { shefIds: string[]; deliveryDateTime: DateTime }) => void;
}

const formatListDeliveryDate = (dateTime: DateTime) => `${dateTime.weekdayLong}, ${dateTime.monthLong} ${dateTime.day}`;

export const EditOrderDeliveryModal = ({ onClose, onDeliveryDateChange }: EditOrderDeliveryModalProps) => {
  const { isSmallScreen } = useScreenContext();

  const { deliveryDate, shefs, shefIds, numShefs, unavailabilities, possibleDeliveryDates } = useSelector((state) => {
    const { cart } = state;
    const { deliveryDate, possibleDeliveryDates } = cart;
    const shefs = values(cart.shefs.entities).filter(isDefined);
    const shefIds = map(shefs, 'id');
    const unavailabilities = selectShefAndFoodItemUnavailabilityCounts(cart, shefIds);

    return { deliveryDate, shefs, shefIds, unavailabilities, numShefs: shefs.length, possibleDeliveryDates };
  });
  const loadedShefs: LoadedCartShef[] = shefs.filter((shef): shef is LoadedCartShef => !shef.loading);

  const [selectedDeliveryDateTime, setSelectedDeliveryDateTime] = useState(() => {
    const [shef] = loadedShefs;
    return urlDateToDateTime(deliveryDate, shef.timeZone);
  });

  const allAvailabilities = possibleDeliveryDates.map((availabilityDate) => {
    const deliveryDateTime = urlDateToDateTime(availabilityDate, DateTime.local().zoneName);
    const formattedDeliveryDate = formatListDeliveryDate(deliveryDateTime);
    return { availabilityDate, deliveryDateTime, formattedDeliveryDate };
  });
  const availabilities = sortBy(uniqBy(allAvailabilities, 'availabilityDate'), 'availabilityDate');

  return (
    <StyledModal
      isShowing
      onClose={onClose}
      showCloseButton
      size={ModalSizeEnum.md}
      isFullWidth={isSmallScreen}
      isFullHeight={isSmallScreen}>
      <Header>Edit Delivery</Header>
      <ModalBody>
        {availabilities.map(({ availabilityDate, deliveryDateTime, formattedDeliveryDate }) => {
          const unavailable = unavailabilities[availabilityDate] || { foodItems: 0, shefs: 0 };

          return (
            <StyledRadioContainer key={availabilityDate}>
              <Radio
                fillColor={Colors.RADISH}
                label={
                  <>
                    <RadioHeader>
                      {formattedDeliveryDate}
                      {numShefs > 1 && (
                        <ShefBadge>
                          <ShefHatIcon /> <ShefCount>{numShefs - unavailable.shefs}</ShefCount>
                        </ShefBadge>
                      )}
                    </RadioHeader>
                    <RadioInfo>
                      <Paragraph
                        size={Font.Size.XXS}
                        color={unavailable.foodItems > 0 ? Colors.RADISH : 'inherit'}
                        margin='0'>
                        {match({
                          numShefs,
                          unavailableShefs: unavailable.shefs,
                          unavailableFoodItems: unavailable.foodItems,
                        })
                          .with(
                            { numShefs: gt(1), unavailableShefs: gt(0), unavailableFoodItems: gt(0) },
                            () =>
                              `${unavailable.shefs} ${unavailable.shefs > 1 ? 'shefs' : 'shef'} and ${
                                unavailable.foodItems
                              } ${unavailable.foodItems > 1 ? 'items' : 'item'} unavailable`
                          )
                          .with(
                            { unavailableFoodItems: gt(0) },
                            () => `${unavailable.foodItems} ${unavailable.foodItems > 1 ? 'items' : 'item'} unavailable`
                          )
                          .otherwise(() => `All items are available`)}
                      </Paragraph>
                    </RadioInfo>
                  </>
                }
                checked={selectedDeliveryDateTime.toMillis() === deliveryDateTime.toMillis()}
                onChange={() => setSelectedDeliveryDateTime(deliveryDateTime)}
              />
            </StyledRadioContainer>
          );
        })}
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          size={ButtonSizes.M}
          onClick={flow(() => onDeliveryDateChange({ shefIds, deliveryDateTime: selectedDeliveryDateTime }), onClose)}
          fluid>
          Save
        </PrimaryButton>
      </ModalFooter>
    </StyledModal>
  );
};
